import { useAtomValue } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';
import { useCallback, useState } from 'react';
import { upsertUserConfigAtom } from '@/atoms/mutations/userConfigs/userConfigs';
import { unwrappedOnboardingConfigAtom, USER_CONFIGS_QUERY_KEY } from '@/atoms/queries/userConfigs';
import { OnboardingItemKey, UserConfigType } from '@/types/userConfigs';

export const useFirstTimeDismissal = (itemKey: OnboardingItemKey) => {
  const onboardingConfig = useAtomValue(unwrappedOnboardingConfigAtom);
  const queryClient = useAtomValue(queryClientAtom);
  const { mutate: upsertUserConfig } = useAtomValue(upsertUserConfigAtom);
  const [isDismissed, setIsDismissed] = useState(false);

  const isLoaded = onboardingConfig !== undefined;
  const hasSeen = !!onboardingConfig?.definition?.[itemKey];
  const isOpen = isLoaded && !hasSeen && !isDismissed;

  const dismiss = useCallback(() => {
    if (!isOpen) return;
    setIsDismissed(true);
    upsertUserConfig(
      [
        {
          configId: onboardingConfig?.configId,
          configDefinition: { ...onboardingConfig?.definition, [itemKey]: true },
          configType: UserConfigType.ONBOARDING,
        },
      ],
      {
        // Invalidate userConfigs so the newly-generated configId is loaded into
        // onboardingConfig — without this, a second dismissal in the same session
        // still sees configId as undefined and the backend creates a duplicate row.
        onSuccess: () => queryClient.invalidateQueries({ queryKey: USER_CONFIGS_QUERY_KEY }),
      },
    );
  }, [onboardingConfig, isOpen, itemKey, upsertUserConfig, queryClient]);

  return { isOpen, dismiss };
};
